import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAIAction } from '../../hooks/useAIAction';
import { useGenerationStatus } from '../../hooks/useGenerationStatus';
import { useCreditsBalance } from '../../api/queries/credits';
import { useBook } from '../../api/queries/books';
import { useUpdateParagraph } from '../../api/queries/books';
import { useAttachEntityImage } from '../../api/queries/entities';
import { StylePresetSelector } from '../editor/StylePresetSelector';

interface GenerationModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'image' | 'audio' | 'text';
  paragraphId?: string;
  bookId?: string;
  chapterId?: string;
  chapterTitle?: string;
  paragraphText?: string;
  seedPrompt?: string;
  defaultVoiceId?: string;
  entityId?: string;
}

const imageCost = 5;
const audioCost = 2;
const textCost = 1;

const TEXT_QUICK_ACTIONS = [
  {
    label: 'Expand',
    instruction: 'Expand this paragraph with more detail and description.',
  },
  {
    label: 'Rewrite',
    instruction: 'Rewrite this paragraph in a more compelling and vivid style.',
  },
  {
    label: 'Continue',
    instruction:
      'Continue writing from where this paragraph ends, maintaining the same voice and tone.',
  },
  {
    label: 'Summarize',
    instruction: 'Summarize this paragraph into a concise, clear version.',
  },
] as const;

export function GenerationModal({
  open,
  onClose,
  mode,
  paragraphId,
  bookId,
  chapterId,
  chapterTitle,
  paragraphText,
  seedPrompt,
  defaultVoiceId,
  entityId,
}: GenerationModalProps) {
  const actionSlug =
    mode === 'image'
      ? 'image-generation'
      : mode === 'audio'
        ? 'audio-generation'
        : 'text-generation';
  const action = useAIAction(actionSlug);
  const [jobId, setJobId] = useState<string | null>(null);
  const { event } = useGenerationStatus(jobId);
  const { data: credits } = useCreditsBalance();
  const queryClient = useQueryClient();
  const bookQuery = useBook(bookId ?? '');
  const updateParagraph = useUpdateParagraph();
  const attachEntityImage = useAttachEntityImage(entityId ?? '');

  // Image mode state
  const [prompt, setPrompt] = useState(seedPrompt ?? '');
  const [aspect, setAspect] = useState('3:4');
  const [style, setStyle] = useState('');

  // Audio mode state
  const [text, setText] = useState(seedPrompt ?? '');
  const [voiceId, setVoiceId] = useState(defaultVoiceId ?? '');
  const [stability, setStability] = useState(0.5);

  // Text mode state
  const [textInstruction, setTextInstruction] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(500);
  const [textContextExpanded, setTextContextExpanded] = useState(false);

  // Text result state
  const [generatedText, setGeneratedText] = useState<string | null>(null);
  const [textEditable, setTextEditable] = useState(false);
  const [editedText, setEditedText] = useState('');

  useEffect(() => {
    if (open) {
      setJobId(null);
      handledJobRef.current = null;
      setPrompt(seedPrompt ?? '');
      setText(seedPrompt ?? '');
      // When in text mode, pre-fill the instruction with seedPrompt so that
      // context-menu presets (expand / rewrite / summarize) appear immediately.
      setTextInstruction(mode === 'text' ? (seedPrompt ?? '') : '');
      setTemperature(0.7);
      setMaxTokens(500);
      setGeneratedText(null);
      setTextEditable(false);
      setEditedText('');
      setTextContextExpanded(false);
    }
  }, [open, seedPrompt, mode]);

  const cost =
    mode === 'image' ? imageCost : mode === 'audio' ? audioCost : textCost;
  const done = event?.status === 'complete';
  const failed = event?.status === 'failed';
  const balance = credits?.balance ?? 0;
  const insufficient = credits !== undefined && balance < cost;

  // Phase 2 W11: show the context the AI will see before the user commits.
  const findSimilar = useAIAction('find-similar');
  const [context, setContext] = useState<
    {
      entity_type: string;
      entity_id: string;
      score: number;
      preview?: string | null;
    }[]
  >([]);
  const [contextDisabled, setContextDisabled] = useState(false);
  useEffect(() => {
    if (!open || !paragraphId) return;
    let cancelled = false;
    (async () => {
      try {
        const outcome = await findSimilar.mutateAsync({
          entity_type: 'paragraph',
          entity_id: paragraphId,
          k: 5,
        });
        if (cancelled) return;
        const r = outcome.result as
          | { disabled?: boolean; results?: typeof context }
          | undefined;
        setContextDisabled(Boolean(r?.disabled));
        setContext(r?.results ?? []);
      } catch {
        /* silent -- context is a hint, not a blocker */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, paragraphId]);

  // Handle text generation results from the event stream
  useEffect(() => {
    if (mode === 'text' && done && event?.payload) {
      const payload = event.payload as
        | { text?: string; generated_text?: string }
        | undefined;
      const resultText = payload?.generated_text ?? payload?.text ?? '';
      if (resultText) {
        setGeneratedText(resultText);
        setEditedText(resultText);
      }
    }
  }, [mode, done, event]);

  // Track whether we've already handled completion for this job to prevent
  // double-attach and effect re-fires from unstable mutation references.
  const handledJobRef = useRef<string | null>(null);

  useEffect(() => {
    if (!done && !failed) return;
    // Guard: only handle each job once
    if (handledJobRef.current === jobId) return;
    handledJobRef.current = jobId;

    queryClient.invalidateQueries({ queryKey: ['credits'] });
    if (paragraphId) {
      queryClient.invalidateQueries({
        queryKey: ['paragraph-generations', paragraphId],
      });
      queryClient.invalidateQueries({
        queryKey: ['paragraph-images', paragraphId],
      });
    }
    if (entityId) {
      // Auto-attach the generated image to the entity
      if (done && mode === 'image' && event?.payload) {
        const payload = event.payload as { image_id?: string } | undefined;
        if (payload?.image_id) {
          void attachEntityImage
            .mutateAsync({ imageId: payload.image_id })
            .then(() => {
              queryClient.invalidateQueries({
                queryKey: ['entity', entityId],
              });
            });
        }
      }
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] });
      queryClient.invalidateQueries({ queryKey: ['images'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, failed, jobId]);

  const buildTextSystemPrompt = (): string => {
    const parts: string[] = [];
    const book = bookQuery.data;
    if (book) {
      parts.push(`Book: "${book.title}"`);
      if (book.pov) parts.push(`POV: ${book.pov}`);
      if (book.tense) parts.push(`Tense: ${book.tense}`);
      if (book.narrativeStyle)
        parts.push(`Narrative style: ${book.narrativeStyle}`);
      if (book.genre) parts.push(`Genre: ${book.genre}`);
    }
    if (chapterTitle) {
      parts.push(`Chapter: "${chapterTitle}"`);
    }
    const pText = paragraphText ?? seedPrompt ?? '';
    if (pText) {
      parts.push(`Current paragraph text:\n${pText}`);
    }
    return parts.join('\n');
  };

  const handleSubmit = async () => {
    if (mode === 'text') {
      const params = {
        prompt: textInstruction,
        system_prompt: buildTextSystemPrompt(),
        max_tokens: maxTokens,
        temperature,
        paragraph_id: paragraphId,
        book_id: bookId,
        chapter_id: chapterId,
      };
      const result = await action.mutateAsync(params);
      setJobId(result.jobId);

      // If the result came back synchronously (no background job)
      if (result.result) {
        const payload = result.result as
          | { text?: string; generated_text?: string }
          | undefined;
        const resultText = payload?.generated_text ?? payload?.text ?? '';
        if (resultText) {
          setGeneratedText(resultText);
          setEditedText(resultText);
        }
      }
    } else if (mode === 'image') {
      const params = {
        prompt,
        aspect_ratio: aspect,
        style: style || undefined,
        paragraph_id: paragraphId,
        entity_id: entityId,
      };
      const result = await action.mutateAsync(params);
      setJobId(result.jobId);
    } else {
      const params = {
        text,
        voice_id: voiceId || undefined,
        stability,
        similarity_boost: 0.75,
        paragraph_id: paragraphId,
      };
      const result = await action.mutateAsync(params);
      setJobId(result.jobId);
    }
  };

  const handleAcceptText = async () => {
    if (!paragraphId) return;
    const finalText = textEditable ? editedText : (generatedText ?? '');
    await updateParagraph.mutateAsync({
      id: paragraphId,
      contentHtml: `<p>${finalText.replace(/\n/g, '</p><p>')}</p>`,
    });
    setGeneratedText(null);
    onClose();
  };

  const handleRejectText = () => {
    setGeneratedText(null);
    setTextEditable(false);
    setEditedText('');
  };

  const handleEditText = () => {
    setTextEditable(true);
  };

  const modalTitle =
    mode === 'image'
      ? 'Generate image'
      : mode === 'audio'
        ? 'Generate audio'
        : 'Generate text';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{modalTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {mode === 'image' && (
            <>
              <TextField
                label="Prompt"
                multiline
                minRows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <FilterableSelect
                options={['1:1', '16:9', '9:16', '4:3', '3:4'].map((ratio) => ({
                  value: ratio,
                  label: ratio,
                }))}
                value={aspect}
                onChange={(newValue) => setAspect(newValue)}
                label="Aspect ratio"
                allowEmpty={false}
              />
              <StylePresetSelector value={style} onChange={setStyle} />
            </>
          )}

          {mode === 'audio' && (
            <>
              <TextField
                label="Text"
                multiline
                minRows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                helperText="For multi-speaker, use [S1] and [S2] tags: [S1] Narrator text. [S2] Character dialogue."
              />
              <TextField
                label="Voice id"
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                helperText="Kokoro: af_heart, am_adam, etc. Dia: leave blank for default multi-speaker."
              />
              <Typography variant="caption">
                Stability: {stability.toFixed(2)}
              </Typography>
              <Slider
                min={0}
                max={1}
                step={0.05}
                value={stability}
                onChange={(_, v) => setStability(v as number)}
              />
            </>
          )}

          {mode === 'text' && (
            <>
              <TextField
                label="What should the AI do?"
                placeholder="e.g., Expand this paragraph, Rewrite in a darker tone"
                multiline
                minRows={3}
                value={textInstruction}
                onChange={(e) => setTextInstruction(e.target.value)}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {TEXT_QUICK_ACTIONS.map((qa) => (
                  <Chip
                    key={qa.label}
                    label={qa.label}
                    variant={
                      textInstruction === qa.instruction ? 'filled' : 'outlined'
                    }
                    color={
                      textInstruction === qa.instruction ? 'primary' : 'default'
                    }
                    onClick={() => setTextInstruction(qa.instruction)}
                    clickable
                    size="small"
                  />
                ))}
              </Stack>
              <Typography variant="caption">
                Creativity: {temperature.toFixed(1)}
              </Typography>
              <Slider
                min={0}
                max={1}
                step={0.1}
                value={temperature}
                onChange={(_, v) => setTemperature(v as number)}
                valueLabelDisplay="auto"
              />
              <TextField
                label="Max tokens"
                type="number"
                value={maxTokens}
                onChange={(e) =>
                  setMaxTokens(Math.max(1, Number(e.target.value) || 500))
                }
                slotProps={{ htmlInput: { min: 1, max: 4000 } }}
                size="small"
              />

              {/* Context display */}
              <Accordion
                expanded={textContextExpanded}
                onChange={(_, expanded) => setTextContextExpanded(expanded)}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: 'divider' }}
              >
                <AccordionSummary>
                  <Typography variant="body2">
                    Context (what the AI sees)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {bookQuery.data && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Book:</strong> {bookQuery.data.title}
                        {bookQuery.data.pov
                          ? ` | POV: ${bookQuery.data.pov}`
                          : ''}
                        {bookQuery.data.tense
                          ? ` | Tense: ${bookQuery.data.tense}`
                          : ''}
                      </Typography>
                    )}
                    {chapterTitle && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Chapter:</strong> {chapterTitle}
                      </Typography>
                    )}
                    {(paragraphText ?? seedPrompt) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          maxHeight: 120,
                          overflow: 'auto',
                        }}
                      >
                        <strong>Paragraph:</strong>{' '}
                        {paragraphText ?? seedPrompt}
                      </Typography>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Text generation result */}
              {generatedText !== null && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Generated text
                  </Typography>
                  {textEditable ? (
                    <TextField
                      multiline
                      fullWidth
                      minRows={4}
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        maxHeight: 300,
                        overflow: 'auto',
                      }}
                    >
                      {generatedText}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={handleAcceptText}
                      disabled={updateParagraph.isPending || !paragraphId}
                    >
                      {updateParagraph.isPending ? 'Saving...' : 'Accept'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={handleRejectText}
                    >
                      Reject
                    </Button>
                    {!textEditable && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleEditText}
                      >
                        Edit
                      </Button>
                    )}
                  </Stack>
                </Paper>
              )}
            </>
          )}

          {/* Relevant context accordion (image/audio modes) */}
          {mode !== 'text' && paragraphId && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <AccordionSummary>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">Relevant context</Typography>
                  {findSimilar.isPending ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Chip
                      size="small"
                      label={
                        contextDisabled
                          ? 'embeddings off'
                          : `${context.length} nearby`
                      }
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {contextDisabled && (
                  <Typography variant="caption" color="text.secondary">
                    Embeddings are disabled; the AI sees prompt text only.
                  </Typography>
                )}
                {!contextDisabled &&
                  context.length === 0 &&
                  !findSimilar.isPending && (
                    <Typography variant="caption" color="text.secondary">
                      Nothing similar yet. Embeddings populate as you write.
                    </Typography>
                  )}
                <List dense disablePadding>
                  {context.map((hit) => (
                    <ListItem
                      key={`${hit.entity_type}:${hit.entity_id}`}
                      disableGutters
                    >
                      <ListItemText
                        primary={`${hit.entity_type} · ${Math.round(hit.score * 100)}% similar`}
                        secondary={hit.preview ?? hit.entity_id}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          <Typography variant="caption" color="text.secondary">
            Cost: {cost} credit{cost !== 1 ? 's' : ''} · current balance:{' '}
            {balance}
          </Typography>
          {insufficient && (
            <Alert severity="warning">
              Insufficient credits. Top up your balance to run this generation.
            </Alert>
          )}

          {jobId && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Job {jobId} · status: {event?.status ?? 'queued'}
              </Typography>
              {!done && !failed && <LinearProgress />}
            </Stack>
          )}
          {failed && (
            <Alert severity="error">
              Generation failed:{' '}
              {(event?.payload as { error?: string } | undefined)?.error}
            </Alert>
          )}
          {done && mode !== 'text' && (
            <Alert severity="success">
              Generation complete
              {paragraphId
                ? ' — linked to paragraph.'
                : entityId
                  ? ' — attached to entity.'
                  : '.'}
            </Alert>
          )}
          {done && mode === 'text' && generatedText === null && (
            <Alert severity="success">Text generation complete.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            action.isPending ||
            Boolean(jobId && !done && !failed) ||
            insufficient ||
            (mode === 'text' && !textInstruction.trim())
          }
        >
          {action.isPending ? 'Submitting...' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
